import { useCallback, useMemo } from 'react';
import { useRemoveUsersPopup } from './useRemoveUserPopup';
import { User } from '../../../types/User';
import { useSetExternalEmailPopup } from './useSetExternalEmailPopup';
import { useUsers } from '../../../context/UserManagementContext';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext';
import { isAccountMatchingEnabled, isMfaModuleEnabled } from '../../../utils';
import { useTestTokensPopup } from './useTestTokensPopup';
import { useMatchUsersPopup } from './useMatchUsersPopup';
import { useDeleteUsersTokensPopup } from './useDeleteUsersTokensPopup';
import { ProtectedEntityType } from '../../../../../api';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { useSetMaximumTokensPopup } from './useSetMaximumTokensPopup';
import useSendEnrollmentRequestPopup from '../../SendEnrollmentRequest/hooks/useSendEnrollmentRequestPopup';
import useResumeProtection from './useResumeProtection';
import { useExcludeUsersPopup } from './useExcludeUserPopup';
import { BulkAction } from '../../BulkActions';

interface Params {
    selectedUsers: User[];
    onClose: () => void;
}

interface useBulkActionsReturnType {
    actions: BulkAction[];
    menuActions: BulkAction[];
}

const useBulkActions = ({ selectedUsers, onClose }: Params): useBulkActionsReturnType => {
    const selectedUsersTokens = useMemo(() => selectedUsers.flatMap((user) => user.tokens), [selectedUsers]);

    const { mutate, userSession, systemData } = useUsers();
    const { pricingModulesState } = usePricingModulesState();

    const { openRemoveUsersPopup } = useRemoveUsersPopup({ users: selectedUsers, onUserRemoved: mutate });
    const { openSetMaximumTokensPopup } = useSetMaximumTokensPopup({ users: selectedUsers, onMaxTokensSet: mutate });
    const { openSendEnrollmentRequestPopup } = useSendEnrollmentRequestPopup({
        systemData,
        userSession,
        recipients: selectedUsers.map((user) => ({ guid: user.guid, type: ProtectedEntityType.USER }))
    });
    const hasAccountMatching = isAccountMatchingEnabled(systemData);

    const isAllUsersExcluded = selectedUsers.every((user) => user.excludeTill);

    const { openMatchUsersPopup } = useMatchUsersPopup({
        onMatchedUsers: useCallback(() => {
            mutate();
            onClose();
        }, [onClose, mutate])
    });
    const mfaModuleEnabled = isMfaModuleEnabled(pricingModulesState);
    const { openTestTokensPopup } = useTestTokensPopup({ tokens: selectedUsersTokens });

    const { openDeleteUsersTokensPopup } = useDeleteUsersTokensPopup({ users: selectedUsers, onTokensDeleted: mutate });

    // Currently commented out as the feature is not implemented yet, will implement in the v6.1.1
    // const { openDisableUsersTokensPopup } = useDisableUsersTokensPopup({
    //     users: selectedUsers,
    //     onTokensDisabled: mutate
    // });

    const { openSetExternalEmailPopup } = useSetExternalEmailPopup({
        users: selectedUsers,
        onSetExternalEmail: mutate
    });

    const { openExcludeUserPopup } = useExcludeUsersPopup({
        users: selectedUsers,
        onUsersExcluded: useCallback(() => {
            mutate();
            onClose();
        }, [onClose, mutate])
    });

    const { handleResumeProtection } = useResumeProtection({
        users: selectedUsers,
        onProtectionResumed: useCallback(() => {
            mutate();
            onClose();
        }, [onClose, mutate])
    });

    const actions: BulkAction[] = useMemo(
        () => [
            ...(hasAccountMatching
                ? [
                    {
                        key: 'matching',
                        label: 'Match users',
                        disabled:
                            selectedUsers.length < 2 ||
                            !isUserPermitted(userSession, 'matchUsers', ROLES.EDITOR.value),
                        onClick: () => {
                            openMatchUsersPopup(selectedUsers);
                        }
                    }
                ]
                : []),
            ...(mfaModuleEnabled
                ? [
                    {
                        key: 'exclusion',
                        label: isAllUsersExcluded ? 'Resume protection' : 'Temporarily exclude',
                        disabled: !isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value),
                        onClick: isAllUsersExcluded ? handleResumeProtection : openExcludeUserPopup
                    }
                ]
                : []),
            {
                key: 'removal',
                label: 'Remove',
                disabled: !isUserPermitted(userSession, 'manageProtectedGroups', ROLES.EDITOR.value),
                onClick: openRemoveUsersPopup
            }
        ],
        [
            openExcludeUserPopup,
            openRemoveUsersPopup,
            userSession,
            openMatchUsersPopup,
            selectedUsers,
            mfaModuleEnabled,
            hasAccountMatching
        ]
    );

    const menuActions: BulkAction[] = useMemo(
        () => [
            {
                key: 'delete_tokens',
                label: 'Delete all tokens',
                disabled: !isUserPermitted(userSession, 'deleteUserTokens', ROLES.EDITOR.value),
                onClick: openDeleteUsersTokensPopup
            },
            {
                key: 'test_tokens',
                label: 'Test tokens',
                disabled: !isUserPermitted(userSession, 'testUserTokens', ROLES.EDITOR.value),
                onClick: openTestTokensPopup
            },
            {
                key: 'send_enrollment',
                label: 'Send enrollment email',
                disabled: !isUserPermitted(userSession, 'sendEnrollmentEmail', ROLES.EDITOR.value),
                onClick: openSendEnrollmentRequestPopup
            },
            {
                key: 'set_external_email',
                label: 'Set as external email',
                disabled: !isUserPermitted(userSession, 'setEmail', ROLES.EDITOR.value),
                onClick: openSetExternalEmailPopup
            },
            {
                key: 'set_max_tokens',
                label: 'Set max tokens',
                onClick: openSetMaximumTokensPopup
            }
        ],
        [
            userSession,
            openDeleteUsersTokensPopup,
            openTestTokensPopup,
            openSendEnrollmentRequestPopup,
            openSetExternalEmailPopup,
            openSetMaximumTokensPopup
        ]
    );

    return { actions, menuActions };
};

export default useBulkActions;
